import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'

export interface Alerta {
  id: number
  tipo: 'SEM_REUNIAO' | 'TAREFA_ATRASADA' | 'SEM_CONTATO_CLIENTE' | 'ETAPA_PARADA' | 'PARCELA_VENCENDO' | 'SEM_ATA'
  mensagem: string
  projeto_id: number | null
  referencia_id: number | null
  referencia_tipo: string | null
  severidade: 'BAIXA' | 'MEDIA' | 'ALTA'
  lido: boolean
  resolvido: boolean
  criado_em: string
  projetos?: { id: number; nome: string; codigo_projeto: string } | null
}

interface Filtros {
  projeto_id?: number
  lido?: boolean
  resolvido?: boolean
  tipo?: string
  limit?: number
}

export function useAlertas(filtros: Filtros = {}) {
  const [alertas, setAlertas] = useState<Alerta[]>([])
  const [loading, setLoading] = useState(true)
  const [countNaoLidos, setCountNaoLidos] = useState(0)

  const load = useCallback(async () => {
    setLoading(true)
    let q = supabase
      .from('alertas')
      .select('*, projetos(id, nome, codigo_projeto)')
      .eq('resolvido', filtros.resolvido ?? false)
      .order('criado_em', { ascending: false })

    if (filtros.projeto_id) q = q.eq('projeto_id', filtros.projeto_id)
    if (filtros.lido !== undefined) q = q.eq('lido', filtros.lido)
    if (filtros.tipo) q = q.eq('tipo', filtros.tipo)
    q = q.limit(filtros.limit ?? 50)

    const { data } = await q
    setAlertas(data ?? [])
    setLoading(false)
  }, [filtros.projeto_id, filtros.lido, filtros.resolvido, filtros.tipo, filtros.limit])

  // Count não lidos separado (para badge)
  const loadCount = useCallback(async () => {
    let q = supabase
      .from('alertas')
      .select('id', { count: 'exact', head: true })
      .eq('lido', false)
      .eq('resolvido', false)

    if (filtros.projeto_id) q = q.eq('projeto_id', filtros.projeto_id)

    const { count } = await q
    setCountNaoLidos(count ?? 0)
  }, [filtros.projeto_id])

  useEffect(() => {
    load()
    loadCount()
  }, [load, loadCount])

  const marcarLido = useCallback(async (id: number) => {
    await supabase.from('alertas').update({ lido: true }).eq('id', id)
    setAlertas(prev => prev.map(a => a.id === id ? { ...a, lido: true } : a))
    setCountNaoLidos(prev => Math.max(0, prev - 1))
  }, [])

  const marcarTodosLidos = useCallback(async () => {
    const ids = alertas.filter(a => !a.lido).map(a => a.id)
    if (!ids.length) return
    await supabase.from('alertas').update({ lido: true }).in('id', ids)
    setAlertas(prev => prev.map(a => ({ ...a, lido: true })))
    setCountNaoLidos(0)
  }, [alertas])

  return { alertas, loading, countNaoLidos, marcarLido, marcarTodosLidos, reload: load }
}
